/**
 * Per-subtype field visibility for a listing's detail view.
 *
 * Every serviceType2 entry returned by /api/servicetypes carries a `fields`
 * list, and an entry with `show: false` hides that field on /view-post (a
 * subtype configured without photos, say, or without a location line).
 *
 * This lived inline in ViewAds.vue. It moved here so the hover preview in the
 * list view can honour exactly the same config — a preview that showed a field
 * the real page deliberately hides would be worse than showing nothing.
 */
import axios from "axios";

// One request per session, shared by every caller. The map is small, global,
// and only changes when an admin edits the service-type config.
let _mapPromise = null;

export function loadServiceTypesMap() {
  if (!_mapPromise) {
    _mapPromise = axios
      .get("/api/servicetypes")
      .then(({ data }) => data.servicetypes2Map || {})
      .catch(() => {
        // Don't cache the failure — the next page load or hover should retry.
        _mapPromise = null;
        return {};
      });
  }
  return _mapPromise;
}

/**
 * Flatten a subtype's `fields` list into { <key>: <visible> }.
 * An unknown category/subtype yields {}, which `isFieldVisible` reads as
 * "no config, show everything".
 */
export function buildFieldVisibility(servicetypes2Map, serviceType1, serviceType2) {
  const subtypeObj = servicetypes2Map?.[serviceType1]?.find(
    (item) => item.name === serviceType2
  );
  const visibility = {};
  if (subtypeObj && subtypeObj.fields) {
    subtypeObj.fields.forEach((f) => {
      visibility[f.key] = f.show !== false;
    });
  }
  return visibility;
}

// Template-facing field name → the key the backend config uses.
const FIELD_KEYS = {
  title: "title",
  description: "description",
  location: "location",
  roomPrice: "roomPrice",
  phoneNumber: "phoneNumber",
  email: "email",
  images: "photo", // map 'images' to 'photo'
  serviceType1: "serviceType1",
  serviceType2: "serviceType2",
};

export function isFieldVisible(visibility, field) {
  // No subtype config → show everything.
  if (!visibility || !Object.keys(visibility).length) return true;

  // Contact group: visible when either half of it is.
  if (field === "contact") {
    return visibility.phoneNumber !== false || visibility.email !== false;
  }

  const backendKey = FIELD_KEYS[field] || field;
  return visibility[backendKey] !== false;
}
